// Lightweight i18n: a flat key→string dictionary per language, a zustand store for
// the current language (persisted), and a useT() hook returning a bound t().
import { create } from 'zustand';

export type Lang = 'en' | 'uk';

type Dict = Record<string, string>;

const en: Dict = {
  'nav.leaderboard': 'Leaderboard',
  'nav.admin': 'Admin',

  'join.title': 'Join the quiz',
  'join.subtitle': 'Enter the game code from the host and pick a nickname.',
  'join.code': 'Game code',
  'join.codePlaceholder': 'e.g. K7QP',
  'join.nickname': 'Nickname',
  'join.namePlaceholder': 'Your name',
  'join.submit': 'Join game',
  'join.pickNickname': 'Game {code} — pick a nickname.',

  'play.connected': 'connected',
  'play.reconnecting': 'reconnecting…',
  'play.joining': 'Joining game {code}…',
  'play.youreIn': "You're in! 🎉",
  'play.waitingHost': 'Waiting for the host to start the quiz.',
  'play.pts': '{n} pts',
  'play.iKnow': 'I KNOW',
  'play.youBuzzed': 'You buzzed!',
  'play.buzzersClosed': 'Buzzers closed — the host is picking.',
  'play.getReady': 'Get ready…',
  'play.waitingNext': 'Waiting for the next question…',
  'play.calledOn': '📣 Host called on {name}',
  'play.correct': '✅ {name} got it right (+{points})',
  'play.wrong': '❌ {name} was wrong',
  'play.buzzedIn': 'Buzzed in:',
  'play.scores': 'Scores',
  'play.gameOver': '🏁 Game over!',
  'play.winner': '🥇 {name} wins with {score} pts',
  'play.leave': 'Leave game',

  'login.title': 'Host sign in',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.newPassword': 'New password',
  'login.firstSignin': 'First sign-in — choose a new password.',
  'login.signin': 'Sign in',
  'login.signingIn': 'Signing in…',
  'login.setPassword': 'Set password & continue',
  'login.failed': 'Sign-in failed',

  'games.title': 'Host dashboard',
  'games.templates': 'Templates',
  'games.authoring': 'Authoring',
  'games.history': 'History',
  'games.signout': 'Sign out',
  'games.newGame': 'New game',
  'games.pickTopics': 'Tap topics in the order you want to play them.',
  'games.noTopics': 'No topics yet.',
  'games.createTopics': 'Create topics',
  'games.filterTopics': 'Filter topics…',
  'games.noMatch': 'No topics match your filter.',
  'games.createGame': 'Create game ({n})',
  'games.creating': 'Creating…',
  'games.recentGames': 'Recent games',
  'games.noGames': 'No games yet.',
  'games.openConsole': 'Open console',
  'games.viewResults': 'View results',
  'games.deleteConfirm': 'Delete game {code}? This removes its players, scores and results permanently.',

  'templates.title': 'Game templates',
  'templates.intro': 'Reusable topic lineups. Set one up once, then start games from it.',
  'templates.new': 'New template',
  'templates.yourTemplates': 'Your templates',
  'templates.none': 'No templates yet. Create one to save a topic lineup.',
  'templates.topicCount': '{n} topics',
  'templates.startGame': 'Start game',
  'templates.deleteConfirm': 'Delete template “{name}”? Topics and games are not affected.',
  'templates.newTitle': 'New template',
  'templates.editTitle': 'Edit template',
  'templates.namePlaceholder': 'Template name',
  'templates.lineup': 'Topics (drag to reorder)',
  'templates.emptyLineup': 'No topics yet — add some below.',
  'templates.addTopics': 'Add topics',
  'templates.allAdded': 'All topics are already in this template.',
  'templates.nameRequired': 'Give the template a name first.',
  'templates.noTopicsLeft': "This template's topics no longer exist.",
  'templates.dragHandle': 'Drag to reorder',

  'status.lobby': 'lobby',
  'status.active': 'active',
  'status.finished': 'finished',
  'qstate.idle': 'idle',
  'qstate.open': 'open',
  'qstate.closed': 'closed',
  'res.correct': 'correct',
  'res.incorrect': 'incorrect',

  'console.backGames': 'Games',
  'console.live': 'live',
  'console.connecting': 'connecting…',
  'console.loading': 'Loading console…',
  'console.join': 'Join:',
  'console.copyLink': 'Copy link',
  'console.copied': '✓ Copied',
  'console.showQR': 'Show QR',
  'console.hideQR': 'Hide QR',
  'console.waitingPlayers': 'Waiting for players…',
  'console.pressStart': 'Press Start game when everyone’s in.',
  'console.startGame': 'Start game',
  'console.answer': 'Answer: {answer}',
  'console.noQuestion': 'No question selected.',
  'console.openBuzzer': 'Open buzzer (30s)',
  'console.resetBuzzer': '🔄 Reset buzzer',
  'console.endGame': 'End game',
  'console.endGameConfirm': 'End the game?',
  'console.gameFinished': 'Game finished.',
  'console.buzzedIn': 'Buzzed in ({n})',
  'console.noBuzzes': 'No buzzes yet.',
  'console.callOn': 'Call on',
  'console.correct': 'correct (+{points})',
  'console.wrong': 'wrong',
  'console.undo': 'Undo',
  'console.tooLate': 'Buzzed more than 5s after the correct answer — can no longer score',
  'console.scores': 'Scores',
  'console.noPlayers': 'No players yet.',
  'console.stateLabel': 'state: {status} / {qstate}',

  'nav.prevTopic': 'Previous topic',
  'nav.prevQuestion': 'Previous question',
  'nav.nextQuestion': 'Next question',
  'nav.nextTopic': 'Next topic',

  'qr.scan': 'Scan to join instantly',

  'authoring.title': 'Authoring',
  'authoring.bulkImport': 'Bulk import',
  'authoring.importHelp': 'Upload a JSON file (see docs/import-template.json). Each topic needs 5 questions.',
  'authoring.imported': 'Imported {imported}, skipped {skipped}.',
  'authoring.topics': 'Topics',
  'authoring.newTopicPlaceholder': 'New topic title',
  'authoring.add': '＋ Add',
  'authoring.filterTopics': 'Filter topics…',
  'authoring.newestFirst': 'Newest first',
  'authoring.titleAZ': 'Title A–Z',
  'authoring.noTopics': 'No topics yet — add one above or import a JSON file.',
  'authoring.noMatch': 'No topics match your filter.',
  'authoring.deleteTopicConfirm': 'Delete this topic and its questions?',
  'authoring.deleteQuestionConfirm': 'Delete this question?',
  'authoring.qCount': '{n} Q',
  'authoring.questionText': 'Question text',
  'authoring.answerHostOnly': 'Answer (host only)',
  'authoring.importFailed': 'Import failed',
  'authoring.failedLoad': 'Failed to load',

  'lb.title': '🏆 All-time leaderboard',
  'lb.loading': 'Loading…',
  'lb.noGames': 'No games have finished yet.',
  'lb.games': '{n} games',

  'history.title': 'Past games',
  'history.noFinished': 'No finished games yet.',
  'history.noPlayers': 'No players.',

  'common.back': '← Games',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.add': 'Add',
};

const uk: Dict = {
  'nav.leaderboard': 'Рейтинг',
  'nav.admin': 'Адмін',

  'join.title': 'Приєднатися до вікторини',
  'join.subtitle': 'Введіть код гри від ведучого та оберіть нікнейм.',
  'join.code': 'Код гри',
  'join.codePlaceholder': 'напр. K7QP',
  'join.nickname': 'Нікнейм',
  'join.namePlaceholder': "Ваше ім'я",
  'join.submit': 'Приєднатися',
  'join.pickNickname': 'Гра {code} — оберіть нікнейм.',

  'play.connected': 'підключено',
  'play.reconnecting': 'перепідключення…',
  'play.joining': 'Приєднання до гри {code}…',
  'play.youreIn': 'Ви в грі! 🎉',
  'play.waitingHost': 'Очікуємо, поки ведучий розпочне вікторину.',
  'play.pts': '{n} балів',
  'play.iKnow': 'Я ЗНАЮ',
  'play.youBuzzed': 'Ви натиснули!',
  'play.buzzersClosed': 'Кнопки закрито — ведучий обирає.',
  'play.getReady': 'Приготуйтеся…',
  'play.waitingNext': 'Очікування наступного питання…',
  'play.calledOn': '📣 Ведучий викликав {name}',
  'play.correct': '✅ {name} відповів правильно (+{points})',
  'play.wrong': '❌ {name} помилився',
  'play.buzzedIn': 'Натиснули:',
  'play.scores': 'Рахунок',
  'play.gameOver': '🏁 Гру завершено!',
  'play.winner': '🥇 {name} перемагає з {score} балами',
  'play.leave': 'Вийти з гри',

  'login.title': 'Вхід для ведучого',
  'login.email': 'Ел. пошта',
  'login.password': 'Пароль',
  'login.newPassword': 'Новий пароль',
  'login.firstSignin': 'Перший вхід — оберіть новий пароль.',
  'login.signin': 'Увійти',
  'login.signingIn': 'Вхід…',
  'login.setPassword': 'Встановити пароль і продовжити',
  'login.failed': 'Помилка входу',

  'games.title': 'Панель ведучого',
  'games.templates': 'Шаблони',
  'games.authoring': 'Редактор',
  'games.history': 'Історія',
  'games.signout': 'Вийти',
  'games.newGame': 'Нова гра',
  'games.pickTopics': 'Торкніться тем у порядку, в якому хочете грати.',
  'games.noTopics': 'Ще немає тем.',
  'games.createTopics': 'Створити теми',
  'games.filterTopics': 'Фільтр тем…',
  'games.noMatch': 'Немає тем за вашим фільтром.',
  'games.createGame': 'Створити гру ({n})',
  'games.creating': 'Створення…',
  'games.recentGames': 'Останні ігри',
  'games.noGames': 'Ще немає ігор.',
  'games.openConsole': 'Відкрити консоль',
  'games.viewResults': 'Результати',
  'games.deleteConfirm': 'Видалити гру {code}? Це назавжди видалить її гравців, бали та результати.',

  'templates.title': 'Шаблони ігор',
  'templates.intro': 'Багаторазові набори тем. Налаштуйте один раз і запускайте ігри з нього.',
  'templates.new': 'Новий шаблон',
  'templates.yourTemplates': 'Ваші шаблони',
  'templates.none': 'Ще немає шаблонів. Створіть шаблон, щоб зберегти набір тем.',
  'templates.topicCount': 'Тем: {n}',
  'templates.startGame': 'Почати гру',
  'templates.deleteConfirm': 'Видалити шаблон «{name}»? Теми та ігри не постраждають.',
  'templates.newTitle': 'Новий шаблон',
  'templates.editTitle': 'Редагувати шаблон',
  'templates.namePlaceholder': 'Назва шаблону',
  'templates.lineup': 'Теми (перетягніть, щоб змінити порядок)',
  'templates.emptyLineup': 'Ще немає тем — додайте нижче.',
  'templates.addTopics': 'Додати теми',
  'templates.allAdded': 'Усі теми вже в цьому шаблоні.',
  'templates.nameRequired': 'Спочатку вкажіть назву шаблону.',
  'templates.noTopicsLeft': 'Теми цього шаблону більше не існують.',
  'templates.dragHandle': 'Перетягнути',

  'status.lobby': 'лобі',
  'status.active': 'активна',
  'status.finished': 'завершена',
  'qstate.idle': 'очікує',
  'qstate.open': 'відкрито',
  'qstate.closed': 'закрито',
  'res.correct': 'правильно',
  'res.incorrect': 'неправильно',

  'console.backGames': 'Ігри',
  'console.live': 'наживо',
  'console.connecting': 'підключення…',
  'console.loading': 'Завантаження консолі…',
  'console.join': 'Приєднання:',
  'console.copyLink': 'Копіювати',
  'console.copied': '✓ Скопійовано',
  'console.showQR': 'Показати QR',
  'console.hideQR': 'Сховати QR',
  'console.waitingPlayers': 'Очікування гравців…',
  'console.pressStart': 'Натисніть «Розпочати гру», коли всі приєднаються.',
  'console.startGame': 'Розпочати гру',
  'console.answer': 'Відповідь: {answer}',
  'console.noQuestion': 'Питання не вибрано.',
  'console.openBuzzer': 'Відкрити кнопку (30с)',
  'console.resetBuzzer': '🔄 Скинути кнопку',
  'console.endGame': 'Завершити гру',
  'console.endGameConfirm': 'Завершити гру?',
  'console.gameFinished': 'Гру завершено.',
  'console.buzzedIn': 'Натиснули ({n})',
  'console.noBuzzes': 'Ще ніхто не натиснув.',
  'console.callOn': 'Викликати',
  'console.correct': 'правильно (+{points})',
  'console.wrong': 'неправильно',
  'console.undo': 'Скасувати',
  'console.tooLate': 'Натиснув(ла) понад 5с після правильної відповіді — бали вже не нараховуються',
  'console.scores': 'Рахунок',
  'console.noPlayers': 'Ще немає гравців.',
  'console.stateLabel': 'стан: {status} / {qstate}',

  'nav.prevTopic': 'Попередня тема',
  'nav.prevQuestion': 'Попереднє питання',
  'nav.nextQuestion': 'Наступне питання',
  'nav.nextTopic': 'Наступна тема',

  'qr.scan': 'Скануйте, щоб приєднатися',

  'authoring.title': 'Редактор',
  'authoring.bulkImport': 'Масовий імпорт',
  'authoring.importHelp': 'Завантажте JSON-файл (див. docs/import-template.json). Кожна тема потребує 5 питань.',
  'authoring.imported': 'Імпортовано {imported}, пропущено {skipped}.',
  'authoring.topics': 'Теми',
  'authoring.newTopicPlaceholder': 'Назва нової теми',
  'authoring.add': '＋ Додати',
  'authoring.filterTopics': 'Фільтр тем…',
  'authoring.newestFirst': 'Спочатку нові',
  'authoring.titleAZ': 'За назвою А–Я',
  'authoring.noTopics': 'Ще немає тем — додайте вище або імпортуйте JSON-файл.',
  'authoring.noMatch': 'Немає тем за вашим фільтром.',
  'authoring.deleteTopicConfirm': 'Видалити цю тему та її питання?',
  'authoring.deleteQuestionConfirm': 'Видалити це питання?',
  'authoring.qCount': '{n} П',
  'authoring.questionText': 'Текст питання',
  'authoring.answerHostOnly': 'Відповідь (лише ведучий)',
  'authoring.importFailed': 'Помилка імпорту',
  'authoring.failedLoad': 'Не вдалося завантажити',

  'lb.title': '🏆 Загальний рейтинг',
  'lb.loading': 'Завантаження…',
  'lb.noGames': 'Ще немає завершених ігор.',
  'lb.games': '{n} ігор',

  'history.title': 'Минулі ігри',
  'history.noFinished': 'Ще немає завершених ігор.',
  'history.noPlayers': 'Немає гравців.',

  'common.back': '← Ігри',
  'common.delete': 'Видалити',
  'common.edit': 'Редагувати',
  'common.save': 'Зберегти',
  'common.cancel': 'Скасувати',
  'common.add': 'Додати',

  // Error messages — keyed by the exact English text the server/client produces,
  // so t(errorMessage) localises them (and falls back to English otherwise).
  'join a game before buzzing': 'приєднайтеся до гри, перш ніж натискати',
  'the buzzer is closed': 'кнопку закрито',
  'too late — the 30 second window has closed': 'занадто пізно — 30-секундне вікно закрилося',
  'code and nickname are required': 'потрібні код і нікнейм',
  'game not found for that code': 'гру за цим кодом не знайдено',
  'this game has already finished': 'цю гру вже завершено',
  'admin action requires an admin connection attached to a game': 'потрібне підключення адміністратора до гри',
  'game not found': 'гру не знайдено',
  'game already started': 'гру вже розпочато',
  'start the game first': 'спершу розпочніть гру',
  'no current question to open': 'немає поточного питання для відкриття',
  'question is already open': 'питання вже відкрито',
  'reset the buzzer (or move on) before opening again': 'скиньте кнопку (або перейдіть далі), перш ніж відкривати знову',
  'warning: buzzer auto-close timer failed to start': 'попередження: не вдалося запустити таймер автозакриття',
  'no more questions in this topic — advance to the next topic': 'більше немає питань у цій темі — перейдіть до наступної',
  'already at the first question — go back a topic': 'це вже перше питання — поверніться до попередньої теми',
  'no more topics — end the game': 'більше немає тем — завершіть гру',
  'already on the first topic': 'це вже перша тема',
  'no current topic': 'немає поточної теми',
  'no current question to reset': 'немає поточного питання для скидання',
  'game has no topics': 'у грі немає тем',
  'current topic has no questions': 'у поточній темі немає питань',
  'invalid join payload': 'некоректні дані приєднання',
  'invalid callOn payload': 'некоректні дані виклику',
  'invalid mark payload': 'некоректні дані оцінки',
  'player not found': 'гравця не знайдено',
  'no active question': 'немає активного питання',
  'that player did not buzz for this question': 'цей гравець не натискав на це питання',
  'this question has already been answered correctly': 'на це питання вже відповіли правильно',
  // REST
  'title is required': 'потрібна назва',
  'text and positive points are required': 'потрібні текст і додатні бали',
  'topicOrder must contain at least one topic': 'потрібна принаймні одна тема',
  'payload must contain at least one topic': 'потрібна принаймні одна тема',
  'not found': 'не знайдено',
  'empty request body': 'порожній запит',
  // Client-side fallbacks
  'Not signed in': 'Ви не увійшли',
  'Failed to load': 'Не вдалося завантажити',
  'Failed to create game': 'Не вдалося створити гру',
  'Failed to delete game': 'Не вдалося видалити гру',
  'Failed to add topic': 'Не вдалося додати тему',
};

const dicts: Record<Lang, Dict> = { en, uk };

function detectLang(): Lang {
  const saved = localStorage.getItem('beernerd.lang');
  if (saved === 'en' || saved === 'uk') return saved;
  // Ukrainian is the default UI language; users can switch to EN and it sticks.
  return 'uk';
}

interface LangStore {
  lang: Lang;
  setLang: (l: Lang) => void;
}

export const useLang = create<LangStore>((set) => ({
  lang: detectLang(),
  setLang: (lang) => {
    localStorage.setItem('beernerd.lang', lang);
    set({ lang });
  },
}));

export type TFunc = (key: string, params?: Record<string, string | number>) => string;

export function useT(): TFunc {
  const lang = useLang((s) => s.lang);
  return (key, params) => {
    let str = dicts[lang][key] ?? en[key] ?? key;
    if (params) {
      for (const [k, v] of Object.entries(params)) str = str.replaceAll(`{${k}}`, String(v));
    }
    return str;
  };
}
